import { useEffect, useRef, useState } from 'react';
import { usePotwierdzenie } from '@/components/OknoPotwierdzenia';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Clock, ImageUp, Loader2, PencilLine, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, FormGrid, ModalError } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/toast';
import { ObrazekMini } from '@/components/ObrazekMini';
import { fetchMachineTasks, type MachineTask } from '@/lib/agents-api';
import { ApiError } from '@/lib/api';
import {
  gdzieLezyObrazek,
  listaObrazkow,
  ponowWysylkeObrazka,
  przypiszObrazekGrupie,
  przypiszObrazekZBiblioteki,
  usunObrazekZBiblioteki,
  wgrajObrazekDoBiblioteki,
  wgrajObrazekGrupy,
  wgrajObrazekTowaru,
  zmienNazwePliku,
} from '@/lib/items-api';
import { useI18n } from '@/lib/i18n';
import { podpowiedzNazwy } from '@/lib/nazwa-zdjecia';
import { useOpoznione } from '@/lib/opoznienie';
import { cn } from '@/lib/utils';

/** Tyle, ile przyjmuje API. Sprawdzamy tu, żeby nie wysyłać kilku megabajtów po to, by dostać 400. */
const LIMIT_BAJTOW = 8 * 1024 * 1024;

/**
 * Po tylu minutach ciszy „czeka" przestaje znaczyć „zaraz weźmie" — automat się nie odzywa.
 * Agent pyta o zadania co kilkanaście sekund, więc pięć minut to już nie kolejka.
 */
const CISZA_MIN = 5;

/** `ItemGroups.IMAGEFILE` / `ItemSubGroups.IMAGEFILE` mają w SPS 64 znaki — towar 255. */
const MAKS_NAZWA_GRUPY = 64;

/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ZDJĘCIE ARTYKUŁU — jedno okno: co jest teraz, wybór nowego i stan wysyłki na automaty.
 *
 *  ⚠️⚠️ PRZEBUDOWANE 24.09.2026 na zgłoszenia, że wgrywanie jest „nieintuicyjne, trudne i źle
 *  opisane". Rozstrzygnięcia użytkownika z tego dnia:
 *    • JEDEN WIDOK zamiast zakładek „Galeria / Wgraj plik" — zdjęcia z portalu na górze (galeria
 *      pierwsza, jak zamówiono 07.09.2026), a pod nimi pole na plik z komputera;
 *    • NAZWA PLIKU PODPOWIADANA z „Nr u dostawcy" (`podpowiedzNazwy`) i schowana pod „Zmień nazwę",
 *      zamiast obowiązkowego pola z opisem indeksu dostawcy;
 *    • „WIDAĆ, NA KOGO CZEKAMY" — zdjęcie zmienia się w SPS dopiero, gdy plik potwierdzą WSZYSTKIE
 *      automaty (`poDomknieciuPaczki`), i ta zasada zostaje. Okno pokazuje więc każdy automat z jego
 *      stanem, a nieudane da się ponowić. Otwarte z listy przy trwającej wysyłce (`wysylka`) zaczyna
 *      właśnie od tego stanu.
 *
 *  ⚠️ NAZWA PLIKU NIE BIERZE SIĘ Z NUMERU ARTYKUŁU. W SPS to indeks katalogowy dostawcy, a jeden
 *  plik bywa współdzielony przez kilka artykułów — te same rękawice w R9 i R10 wskazują
 *  `P04966881.jpg`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export function ObrazekTowaru({
  towarId,
  grupaId,
  islandId,
  numer,
  nazwa,
  indeks,
  obecny,
  maBajty,
  wysylka = null,
  onClose,
}: {
  towarId?: string;
  /**
   * OBRAZEK GRUPY ARTYKUŁÓW zamiast zdjęcia towaru (27.10.2026) — id węzła z drzewa grup. To samo okno,
   * bo to ta sama wysyłka na automaty; różni się tylko wiersz SPS, do którego trafi nazwa, i jej limit.
   */
  grupaId?: string;
  islandId: string;
  numer: string;
  nazwa: string;
  /**
   * `Items.SUPPLIERPARTNUMBER` — numer u dostawcy. Z niego podpowiadamy nazwę pliku i od niego
   * zaczyna się szukanie w portalu: 42 z 48 zdjęć na DBW nazywa się dokładnie tak.
   */
  indeks: string | null;
  obecny: string | null;
  /** Czy portal ma bajty pliku `obecny` — patrz `hasImage` na wierszu listy. */
  maBajty: boolean;
  /** Trwająca wysyłka zdjęcia tego artykułu — okno zaczyna wtedy od jej stanu. */
  wysylka?: { batchId: string } | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();

  /** Pusty napis znaczy „brak" tak samo jak `null` — u klienta puste pole bywa spacją. */
  const indeksTxt = indeks?.trim() ?? '';

  const [zBiblioteki, setZBiblioteki] = useState<string | null>(null);
  /**
   * ⚠️ SZUKANIE STARTUJE OD „Nr u dostawcy". Galeria i tak zaznacza pierwszy wynik, więc przy
   * zdjęciu nazwanym tym numerem okno otwiera się z gotowym wyborem — zostaje kliknąć „Wyślij".
   * Fraza mieszka tutaj, a nie w galerii, bo ustawia ją też przycisk z numerem w nagłówku.
   */
  const [szukajWGalerii, setSzukajWGalerii] = useState(indeksTxt);

  const [plik, setPlik] = useState<{ nazwa: string; dane: string; podglad: string; bajtow: number } | null>(null);
  const [nazwaPliku, setNazwaPliku] = useState('');
  const [edycjaNazwy, setEdycjaNazwy] = useState(false);
  const [nadPolem, setNadPolem] = useState(false);
  const plikRef = useRef<HTMLInputElement>(null);

  const [batchId, setBatchId] = useState<string | null>(wysylka?.batchId ?? null);
  /** ustawiane, gdy API zapisało plik pod inną nazwą niż podana (dopasowanie rozszerzenia do treści) */
  const [poprawka, setPoprawka] = useState<{ z: string; na: string; format: string } | null>(null);
  const [blad, setBlad] = useState<string | null>(null);

  /** Stan wysyłki — odpytujemy tylko wtedy, gdy jest co śledzić. */
  const paczka = useQuery({
    queryKey: ['machine-tasks', islandId, batchId],
    // ⚠️ `batchId` przekazany DO ZAPYTANIA, nie tylko do klucza pamięci podręcznej — patrz
    // komentarz przy `fetchMachineTasks`. Bez tego postęp liczył wszystkie wgrania na wyspie.
    queryFn: () => fetchMachineTasks(islandId, batchId ?? undefined),
    enabled: batchId !== null,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((z) => z.status !== 'DONE' && z.status !== 'FAILED') ? 3_000 : false,
  });

  const moje = (paczka.data ?? []).filter((z) => z.kind === 'OBRAZ_WGRAJ');
  const gotowych = moje.filter((z) => z.status === 'DONE').length;
  const bledy = moje.filter((z) => z.status === 'FAILED');
  const wszedzie = moje.length > 0 && gotowych === moje.length;

  /** ⚠️ Znacznik przy artykule czyta osobną listę wysyłek — bez jej odświeżenia wisiałby dalej. */
  const odswiezListy = async () => {
    await qc.invalidateQueries({ queryKey: ['items'] });
    await qc.invalidateQueries({ queryKey: ['item-groups'] });
    await qc.invalidateQueries({ queryKey: ['obrazki-wysylki', islandId] });
  };
  useEffect(() => {
    if (!wszedzie) return;
    void qc.invalidateQueries({ queryKey: ['items'] });
    void qc.invalidateQueries({ queryKey: ['item-groups'] });
    void qc.invalidateQueries({ queryKey: ['obrazki-wysylki', islandId] });
  }, [wszedzie, qc, islandId]);

  const wczytaj = (f: File) => {
    setBlad(null);
    if (f.size > LIMIT_BAJTOW) {
      setBlad(t('obraz.zaDuzy').replace('{n}', String(Math.round(LIMIT_BAJTOW / 1_048_576))));
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const url = String(r.result);
      setPlik({ nazwa: f.name, dane: url.slice(url.indexOf(',') + 1), podglad: url, bajtow: f.size });
      /** ⚠️ Jedno źródło naraz: plik z dysku zastępuje wybór z portalu, a nie stoi obok niego. */
      setZBiblioteki(null);
      setNazwaPliku(podpowiedzNazwy(indeksTxt, f.name));
      setEdycjaNazwy(false);
    };
    r.readAsDataURL(f);
  };

  const poWyslaniu = async (r: { batchId: string; automatow: number; nazwaPliku: string }) => {
    setBatchId(r.batchId);
    setNazwaPliku(r.nazwaPliku);
    toast.success(t('obraz.wyslany').replace('{n}', String(r.automatow)));
    await odswiezListy();
  };

  const wyslij = useMutation({
    mutationFn: () => {
      const dane = { groupId: islandId, nazwaPliku: nazwaPliku.trim(), dane: plik!.dane };
      return grupaId !== undefined ? wgrajObrazekGrupy(grupaId, dane) : wgrajObrazekTowaru(towarId!, dane);
    },
    onSuccess: async (r) => {
      /**
       * ⚠️ NAZWA Z ODPOWIEDZI, NIE Z POLA. API dopasowuje rozszerzenie do TREŚCI pliku, więc przy
       * PNG-u nazwanym `.jpg` zapisze `.png` — i mówimy to wprost, zamiast podmieniać po cichu.
       */
      if (r.poprawionoZ !== null) setPoprawka({ z: r.poprawionoZ, na: r.nazwaPliku, format: r.format });
      await poWyslaniu(r);
    },
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  /**
   * PRZYPISANIE ZDJĘCIA, KTÓRE PORTAL JUŻ MA.
   *
   * ⚠️ TO NADAL WYSYŁA PLIK NA AUTOMATY, mimo że bajty mamy u siebie — biblioteka powstaje ze
   * zgrania z JEDNEGO automatu, więc pozostałe mogą tego pliku nie mieć.
   */
  const przypisz = useMutation({
    mutationFn: () =>
      grupaId !== undefined
        ? przypiszObrazekGrupie(grupaId, { groupId: islandId, nazwaPliku: zBiblioteki! })
        : przypiszObrazekZBiblioteki(towarId!, { groupId: islandId, nazwaPliku: zBiblioteki! }),
    onSuccess: poWyslaniu,
    onError: (e: Error) => setBlad(e instanceof ApiError ? e.message : t('common.error')),
  });

  const ponow = useMutation({
    mutationFn: () => ponowWysylkeObrazka(islandId, batchId!),
    onSuccess: async (r) => {
      toast.success(t('obraz.ponowiono').replace('{n}', String(r.ponowiono)));
      await paczka.refetch();
      await odswiezListy();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  OSTRZEŻENIE PRZED ZASTĄPIENIEM PLIKU NA AUTOMATACH.
   *
   *  ⚠️⚠️ ZGŁOSZONE (31.08.2026): automat 3 może mieć pod tą samą nazwą INNY plik. Wysłanie zapisuje
   *  plik w katalogu grafik każdego automatu, czyli zastępuje ten, który tam był — bez śladu.
   *
   *  ⚠️ ODPOWIEDŹ Z OSTATNIEGO SPRAWDZENIA, nie z pytania automatów na żywo — dlatego okno mówi też,
   *  których nie sprawdzono: „nie wiem, co tam leży" to inna odpowiedź niż „nic tam nie leży".
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  const docelowaNazwa = (plik !== null ? nazwaPliku : (zBiblioteki ?? '')).trim();
  const nazwaDoPytania = useOpoznione(docelowaNazwa, 300);
  const gdzieLezy = useQuery({
    queryKey: ['obrazek-gdzie-lezy', islandId, nazwaDoPytania],
    queryFn: () => gdzieLezyObrazek(islandId, nazwaDoPytania),
    enabled: nazwaDoPytania !== '' && batchId === null,
  });
  /** ⚠️ Zgoda znika przy KAŻDEJ zmianie nazwy — inaczej odhaczone raz „zastąp" jechałoby dalej. */
  const [zgodaNadpisz, setZgodaNadpisz] = useState(false);
  useEffect(() => setZgodaNadpisz(false), [docelowaNazwa]);
  const nadpisze = gdzieLezy.data?.maszyny ?? [];
  const trzebaZgody = nadpisze.length > 0 && nazwaDoPytania === docelowaNazwa;

  const wToku = wyslij.isPending || przypisz.isPending;
  /** ⚠️ Za długa nazwa dla grupy odpadłaby dopiero na serwerze — mówimy to przed wysłaniem. */
  const zaDlugaDlaGrupy = grupaId !== undefined && docelowaNazwa.length > MAKS_NAZWA_GRUPY;
  const mozna =
    batchId === null &&
    !wToku &&
    !zaDlugaDlaGrupy &&
    (plik !== null ? nazwaPliku.trim() !== '' : zBiblioteki !== null) &&
    (!trzebaZgody || zgodaNadpisz);

  /** Stan jednego automatu słowami — „czeka" rozróżnia kolejkę od automatu, który milczy. */
  const opisStanu = (z: MachineTask): string => {
    if (z.status === 'DONE') return t('obraz.stan.DONE');
    if (z.status === 'FAILED') return `${t('obraz.stan.FAILED')}: ${z.error ?? ''}`;
    if (z.status === 'SENT' || z.status === 'RUNNING') return t('obraz.stan.W_TOKU');
    const widziany = z.agent.lastSeenAt ?? null;
    if (widziany !== null && Date.now() - new Date(widziany).getTime() > CISZA_MIN * 60_000) {
      return t('obraz.stan.OFFLINE').replace('{kiedy}', new Date(widziany).toLocaleString('pl-PL'));
    }
    return t('obraz.stan.QUEUED');
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={grupaId !== undefined ? t('obraz.tytulGrupy') : t('obraz.tytul')}
        description={
          /*
            ⚠️ NUMER ARTYKUŁU TYLKO WTEDY, GDY MÓWI COŚ INNEGO NIŻ NAZWA — na DBW `ITEMNUMBER` jest
            równy `DESCR` we wszystkich 48 towarach, więc „nazwa · numer" wypisywało to samo dwa razy.
          */
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 truncate">{nazwa}</span>
            {numer !== nazwa && <span className="font-mono text-xs">· {numer}</span>}
            {indeksTxt !== '' && (
              /* ⚠️ Przycisk, nie napis: przywraca szukanie po numerze, gdy ktoś wpisał co innego. */
              <button
                type="button"
                disabled={batchId !== null || plik !== null}
                title={t('obraz.indeksHint')}
                onClick={() => setSzukajWGalerii(indeksTxt)}
                className="rounded border border-border px-1.5 py-0.5 text-xs transition-colors hover:bg-accent disabled:opacity-50"
              >
                {t('obraz.indeks')} <span className="font-mono">{indeksTxt}</span>
              </button>
            )}
          </span>
        }
      >
        <FormGrid>
          {/* ── TERAZ ─────────────────────────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 rounded-md border border-border p-2">
            {obecny !== null ? (
              <>
                <ObrazekMini groupId={islandId} nazwaPliku={obecny} hasImage={maBajty} />
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{t('obraz.teraz')}</p>
                  <p className="truncate font-mono text-xs">{obecny}</p>
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">{grupaId !== undefined ? t('obraz.brakObrazkaGrupy') : t('obraz.brakZdjecia')}</p>
            )}
          </div>

          {batchId !== null ? (
            /* ── STAN WYSYŁKI — każdy automat osobno ─────────────────────────────────── */
            <div className="space-y-2 rounded-md border border-border p-3 text-xs">
              <p className="flex items-center gap-2 font-medium">
                {!wszedzie && bledy.length === 0 && <Loader2 className="size-3.5 animate-spin" />}
                {wszedzie
                  ? t('obraz.gotowe')
                  : t('obraz.postep').replace('{n}', String(gotowych)).replace('{m}', String(moje.length))}
              </p>
              <ul className="space-y-1">
                {moje.map((z) => (
                  <li key={z.id} className="flex items-start gap-2">
                    {z.status === 'DONE' ? (
                      <Check className="mt-0.5 size-3.5 shrink-0 text-[var(--success)]" />
                    ) : z.status === 'FAILED' ? (
                      <X className="mt-0.5 size-3.5 shrink-0 text-[var(--destructive)]" />
                    ) : (
                      <Clock className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                    )}
                    <span className={cn('min-w-0 break-words', z.status === 'FAILED' && 'text-[var(--destructive)]')}>
                      <span className="font-medium">{z.agent.machine?.name ?? z.agent.nodeId}</span> — {opisStanu(z)}
                    </span>
                  </li>
                ))}
              </ul>
              {/* ⚠️ Zasada „dopiero po wszystkich" zostaje — mówimy ją, żeby czekanie nie wyglądało na awarię. */}
              {!wszedzie && <p className="text-muted-foreground">{t('obraz.zmieniSie')}</p>}
              {poprawka !== null && (
                <p className="text-muted-foreground">
                  {t('obraz.poprawionoNazwe')
                    .replace('{z}', poprawka.z)
                    .replace('{na}', poprawka.na)
                    .replace('{format}', poprawka.format)}
                </p>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                {bledy.length > 0 && (
                  <Button type="button" size="sm" variant="outline" disabled={ponow.isPending} onClick={() => ponow.mutate()}>
                    {ponow.isPending ? <Loader2 className="animate-spin" /> : <RotateCcw className="size-4" />}
                    {t('obraz.ponow')}
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setBatchId(null);
                    setPoprawka(null);
                    setPlik(null);
                  }}
                >
                  {t('obraz.wybierzInne')}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {plik === null ? (
                <>
                  {/* ── Z PORTALU — pierwsze, bo portal ma zwykle to zdjęcie (07.09.2026) ───── */}
                  <p className="text-xs font-medium">{t('obraz.zPortalu')}</p>
                  <Galeria
                    islandId={islandId}
                    wybrany={zBiblioteki}
                    onWybierz={setZBiblioteki}
                    obecny={obecny}
                    szukaj={szukajWGalerii}
                    onSzukaj={setSzukajWGalerii}
                  />

                  {/* ── Z KOMPUTERA — przeciągnij albo kliknij ─────────────────────────────── */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => plikRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        plikRef.current?.click();
                      }
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setNadPolem(true);
                    }}
                    onDragLeave={() => setNadPolem(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setNadPolem(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f !== undefined) wczytaj(f);
                    }}
                    className={cn(
                      'flex cursor-pointer flex-col items-center gap-1 rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground transition-colors hover:bg-accent focus-visible:outline-2 focus-visible:outline-[var(--primary)]',
                      nadPolem && 'border-[var(--primary)] bg-accent',
                    )}
                  >
                    <ImageUp className="size-5" />
                    <span className="font-medium text-foreground">{t('obraz.zKomputera')}</span>
                    <span>{t('obraz.upusc')}</span>
                  </div>
                  {/* ⚠️ POLE POZA STREFĄ: klik z pola wracałby do strefy i otwierał wybór pliku drugi raz. */}
                  <input
                    ref={plikRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/bmp"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = '';
                      if (f !== undefined) wczytaj(f);
                    }}
                  />
                </>
              ) : (
                /* ── WYBRANY PLIK Z DYSKU ─────────────────────────────────────────────────── */
                <div className="flex items-start gap-3 rounded-md border border-border p-3 text-xs">
                  <img src={plik.podglad} alt="" className="size-20 shrink-0 rounded border border-border object-contain" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <p className="truncate text-muted-foreground">
                      {plik.nazwa} · {Math.round(plik.bajtow / 1024)} kB
                    </p>
                    {edycjaNazwy ? (
                      <>
                        <Input
                          className="h-8 font-mono text-xs"
                          autoComplete="off"
                          aria-label={t('obraz.nazwa')}
                          value={nazwaPliku}
                          onChange={(e) => setNazwaPliku(e.target.value)}
                        />
                        <p className="text-muted-foreground">{t('obraz.nazwaHint')}</p>
                      </>
                    ) : (
                      <p className="flex flex-wrap items-center gap-x-2">
                        <span>
                          {t('obraz.zapiszeJako')} <span className="font-mono">{nazwaPliku}</span>
                        </span>
                        <button
                          type="button"
                          className="text-[var(--accent)] hover:underline"
                          onClick={() => setEdycjaNazwy(true)}
                        >
                          {t('obraz.zmienNazwePliku')}
                        </button>
                      </p>
                    )}
                  </div>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setPlik(null)}>
                    {t('obraz.wybierzInne')}
                  </Button>
                </div>
              )}

              {/* ── OSTRZEŻENIE O ZASTĄPIENIU ───────────────────────────────────────────────── */}
              {trzebaZgody && (
                <div className="space-y-2 rounded-md border border-[var(--warning)] bg-[var(--warning)]/5 p-3 text-xs">
                  <p className="flex items-start gap-1.5">
                    <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--warning)]" />
                    <span>{t('obraz.nadpiszeOstrzezenie').replace('{plik}', docelowaNazwa)}</span>
                  </p>
                  <ul className="ml-5 list-disc space-y-0.5">
                    {nadpisze.map((m) => (
                      <li key={m.agentId}>
                        {m.automat?.nazwa ?? m.nodeId} — {Math.round(m.rozmiar / 1024)} kB
                      </li>
                    ))}
                  </ul>
                  {(gdzieLezy.data?.niezmierzone.length ?? 0) > 0 && (
                    <p className="text-muted-foreground">
                      {t('obraz.nadpiszeNiezmierzone').replace(
                        '{maszyny}',
                        (gdzieLezy.data?.niezmierzone ?? []).map((m) => m.automat?.nazwa ?? m.nodeId).join(', '),
                      )}
                    </p>
                  )}
                  <label className="flex items-center gap-2 font-medium">
                    <input type="checkbox" checked={zgodaNadpisz} onChange={(e) => setZgodaNadpisz(e.target.checked)} />
                    {t('obraz.nadpiszZgoda')}
                  </label>
                </div>
              )}
            </>
          )}

          {zaDlugaDlaGrupy && batchId === null && (
            <p className="text-xs text-[var(--destructive)]">
              {t('obraz.zaDlugaDlaGrupy').replace('{n}', String(MAKS_NAZWA_GRUPY))}
            </p>
          )}
          <ModalError>{blad}</ModalError>
        </FormGrid>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {batchId === null ? t('common.cancel') : t('common.close')}
          </Button>
          {batchId === null && (
            <Button type="button" disabled={!mozna} onClick={() => (plik !== null ? wyslij.mutate() : przypisz.mutate())}>
              {wToku ? <Loader2 className="animate-spin" /> : <ImageUp className="size-4" />}
              {t('obraz.wyslij')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * GALERIA — wybór spośród obrazków, które portal już ma.
 *
 * ⚠️ SZUKANIE JEST FILTREM LISTY, nie osobnym polem obok niej. Ten sam układ, co przy pracowniku
 * w raportach i z tego samego powodu: zgłoszone wprost, że dodatkowa szukajka obok listy wyboru
 * to jedno pole za dużo.
 *
 * ⚠️ PODGLĄD POD SPODEM, NIE MINIATURY W LIŚCIE. Katalog grafik u klienta ma zmierzone ~1500
 * plików; siatka miniatur to tyle samo żądań i tyle samo ściągniętych obrazków, żeby wybrać jeden.
 * Lista jest tekstowa, a bajty ciągniemy WYŁĄCZNIE dla pozycji, którą ktoś wskazał.
 */
/**
 * ⚠️ EKSPORTOWANA, ŻEBY NIE POWSTAŁA DRUGA GALERIA. Okno „Zarządzaj zdjęciami" pokazuje tę samą
 * bibliotekę; osobna implementacja rozjechałaby się przy pierwszej zmianie (ucięcie listy, komunikat
 * o pustej bibliotece, rozmiar miniatury) i dwa ekrany mówiłyby o tym samym pliku co innego.
 */
/**
 * PLIK Z DYSKU JAKO BASE64 — bez prefiksu `data:`.
 *
 * ⚠️ TA SAMA POSTAĆ, JAKIEJ CHCE API (`dane` w `WgrajDoBibliotekiSchema`): sam ładunek, bez
 * nagłówka `data:image/...;base64,`. Pomyłka tutaj kończy się odmową „to nie jest obrazek",
 * bo pierwsze bajty po dekodowaniu są wtedy tekstem nagłówka.
 */
async function plikDoBase64(f: File): Promise<string> {
  return new Promise<string>((zrob, odrzuc) => {
    const r = new FileReader();
    r.onload = () => {
      const url = String(r.result);
      zrob(url.slice(url.indexOf(',') + 1));
    };
    r.onerror = () => odrzuc(new Error('nie udało się odczytać pliku z dysku'));
    r.readAsDataURL(f);
  });
}

export function Galeria({
  islandId,
  wybrany,
  onWybierz,
  obecny,
  szukaj,
  onSzukaj,
  mozeUsuwac = false,
  zarzadzanie = false,
}: {
  islandId: string;
  wybrany: string | null;
  onWybierz: (nazwa: string | null) => void;
  /** Obrazek już przypisany temu towarowi — zaznaczamy go, żeby było widać, co jest teraz. */
  obecny: string | null;
  /** Fraza jest STANEM RODZICA — patrz `szukajWGalerii`: ustawia ją też przycisk z indeksem. */
  szukaj: string;
  onSzukaj: (fraza: string) => void;
  /**
   * Czy wolno usunąć plik Z BIBLIOTEKI (i wyczyścić wskaźnik u towarów).
   *
   * ⚠️ DOMYŚLNIE NIE, i to nie jest ostrożność na zapas: przy PRZYPISYWANIU obrazka do towaru
   * kasowanie z tej samej listy to gotowa pomyłka o jeden wiersz. Włącza to okno „Zarządzaj
   * zdjęciami", gdzie porządkowanie biblioteki jest tym, po co się przyszło.
   */
  mozeUsuwac?: boolean;
  /**
   * Czy galeria jest OSOBNYM WIDOKIEM, w którym porządkuje się bibliotekę.
   *
   * ⚠️⚠️ ZGŁOSZONE (31.08.2026): „galerię robimy jako osobna zakładka, więcej info o pliku
   * konkretnym, na ilu automatach jest używany itp., w galerii opcja dodania pliku z komputera".
   * W oknie PRZYPISYWANIA obrazka to wszystko byłoby szumem — tam galeria jest wybieraczką
   * i ma pokazać listę oraz podgląd, nic więcej. Stąd flaga, a nie dwa komponenty.
   */
  zarzadzanie?: boolean;
}) {
  const { t } = useI18n();
  const { zapytaj, oknoPytania } = usePotwierdzenie();
  const toast = useToast();
  const qc = useQueryClient();
  const plikRef = useRef<HTMLInputElement>(null);
  const [nowaNazwa, setNowaNazwa] = useState('');
  /** ⚠️ DOMYŚLNIE ZAZNACZONY — wymóg wprost ze zgłoszenia; uzasadnienie w `zmienNazwePliku`. */
  const [takzePrzypisane, setTakzePrzypisane] = useState(true);
  /** ⚠️ Filtrowanie idzie do serwera, więc pytamy dopiero, gdy palce przestaną pisać. */
  const fraza = useOpoznione(szukaj, 250);

  const biblioteka = useQuery({
    queryKey: ['obrazki', islandId, fraza],
    queryFn: () => listaObrazkow(islandId, fraza),
  });

  const pliki = biblioteka.data?.pliki ?? [];
  const podglad = wybrany ?? null;

  /**
   * ══════════════════════════════════════════════════════════════════════════════════════════
   *  ⚠️⚠️ SZUKANIE SAMO ZAZNACZA PIERWSZY WYNIK — zgłoszone (31.08.2026): „możemy coś zaznaczyć
   *  i zacząć szukać i nawet jak jest pojedynczy wynik, to wyświetla się nadal stary zaznaczony
   *  plik". Podgląd pokazywał wtedy obrazek, którego na liście już NIE MA, i wyglądało to jak
   *  wynik szukania.
   *
   *  ⚠️ TYLKO PRZY NIEPUSTEJ FRAZIE i tylko wtedy, gdy zaznaczonego NIE MA w wyniku. Zaznaczanie
   *  przy pustej frazie przestawiałoby wybór przy samym otwarciu okna — a w oknie przypisywania
   *  obrazka wybór jest tym, co za chwilę pójdzie na automaty.
   * ══════════════════════════════════════════════════════════════════════════════════════════
   */
  useEffect(() => {
    if (fraza.trim() === '' || biblioteka.data === undefined) return;
    const lista = biblioteka.data.pliki;
    if (lista.length === 0) return;
    if (wybrany !== null && lista.some((p) => p.nazwaPliku === wybrany)) return;
    onWybierz(lista[0]!.nazwaPliku);
  }, [fraza, biblioteka.data, wybrany, onWybierz]);

  /**
   * ⚠️ USUNIĘCIE PYTA LICZBĄ TOWARÓW, nie samą nazwą pliku. Nazwa (`P04966881.jpg`) nie mówi
   * człowiekowi nic; „używa go 5 towarów" mówi, ile ekranów automatu zrobi się pustych.
   */
  /**
   * SZCZEGÓŁY WYBRANEGO PLIKU — na ilu automatach leży i które towary go wskazują.
   *
   * ⚠️⚠️ TO JEST ODPOWIEDŹ NA „więcej info o pliku konkretnym". Nazwa `P04966881.jpg` nie mówi
   * człowiekowi NICZEGO: dopiero „używają go rękawice R9 i R10, leży na dwóch z trzech automatów"
   * pozwala poznać plik i zdecydować, czy wolno go ruszyć.
   *
   * ⚠️ PYTAMY TYLKO W TRYBIE ZARZĄDZANIA i tylko gdy coś wybrano — w oknie przypisywania to byłoby
   * dodatkowe zapytanie na każde kliknięcie w listę, bez żadnej decyzji, którą by wspierało.
   */
  const szczegoly = useQuery({
    queryKey: ['obrazek-gdzie-lezy', islandId, wybrany],
    queryFn: () => gdzieLezyObrazek(islandId, wybrany!),
    enabled: zarzadzanie && wybrany !== null,
  });

  const dodaj = useMutation({
    mutationFn: async (plik: File) => {
      const dane = await plikDoBase64(plik);
      return wgrajObrazekDoBiblioteki(islandId, plik.name, dane);
    },
    onSuccess: async (r) => {
      toast.success(
        (r.nadpisano ? t('obraz.dodanoNadpisano') : t('obraz.dodano'))
          .replace('{plik}', r.nazwaPliku)
          .replace('{kb}', String(Math.round(r.rozmiar / 1024))),
      );
      /** ⚠️ Zaznaczamy plik, który właśnie wszedł — inaczej trzeba go szukać na liście. */
      onWybierz(r.nazwaPliku);
      onSzukaj(r.nazwaPliku);
      await qc.invalidateQueries({ queryKey: ['obrazki'] });
      await qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const zmienNazwe = useMutation({
    mutationFn: (v: { z: string; na: string; takze: boolean }) => zmienNazwePliku(islandId, v.z, v.na, v.takze),
    onSuccess: async (r) => {
      toast.success(
        (r.przepisano > 0 ? t('obraz.nazwaZmienionaZPrzypisaniem') : t('obraz.nazwaZmieniona'))
          .replace('{z}', r.nazwaPliku)
          .replace('{na}', r.nowaNazwa)
          .replace('{n}', String(r.przepisano))
          .replace('{automatow}', String(r.automatow)),
      );
      setNowaNazwa('');
      onWybierz(r.nowaNazwa);
      await qc.invalidateQueries({ queryKey: ['obrazki'] });
      await qc.invalidateQueries({ queryKey: ['obrazek-gdzie-lezy'] });
      await qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
      await qc.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const usun = useMutation({
    mutationFn: (nazwa: string) => usunObrazekZBiblioteki(islandId, nazwa),
    onSuccess: async (r) => {
      toast.success(
        t('obraz.usunieto').replace('{plik}', r.nazwaPliku).replace('{n}', String(r.wyczyszczonoTowarow)),
      );
      if (wybrany === r.nazwaPliku) onWybierz(null);
      await qc.invalidateQueries({ queryKey: ['obrazki'] });
      await qc.invalidateQueries({ queryKey: ['pokrycie-obrazkow'] });
      await qc.invalidateQueries({ queryKey: ['items'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="space-y-3">
      {oknoPytania}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-48 flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            autoComplete="off"
            placeholder={t('obraz.galeriaSzukaj')}
            value={szukaj}
            onChange={(e) => onSzukaj(e.target.value)}
          />
        </div>
        {/*
          ⚠️ DODANIE PLIKU DO SAMEJ BIBLIOTEKI, bez towaru i bez rozsyłania na automaty (zgłoszone
          31.08.2026). Ukryte pole pliku, bo natywnego `<input type="file">` nie da się ostylować
          jak resztę przycisków — ta sama sztuczka co na ekranie Kopie.
        */}
        {zarzadzanie && (
          <>
            <input
              ref={plikRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/bmp"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f !== undefined) dodaj.mutate(f);
              }}
            />
            <Button type="button" size="sm" variant="outline" disabled={dodaj.isPending} onClick={() => plikRef.current?.click()}>
              {dodaj.isPending ? <Loader2 className="animate-spin" /> : <ImageUp className="size-4" />}
              {t('obraz.dodajZKomputera')}
            </Button>
          </>
        )}
      </div>

      <div className="max-h-52 overflow-y-auto rounded-md border border-border">
        {biblioteka.isPending && <p className="p-3 text-xs text-muted-foreground">{t('common.loading')}</p>}
        {biblioteka.isError && (
          <p className="p-3 text-xs text-destructive">
            {t('common.error')}: {(biblioteka.error as Error).message}
          </p>
        )}
        {/*
          ⚠️ PUSTA BIBLIOTEKA TO NIE BŁĄD, tylko stan sprzed zgrania obrazków z automatu — i trzeba
          to powiedzieć, bo inaczej pusta ramka czyta się jak zepsuta galeria.
        */}
        {!biblioteka.isPending && !biblioteka.isError && pliki.length === 0 && (
          <p className="p-3 text-xs text-muted-foreground">
            {t(szukaj === '' ? 'obraz.galeriaPusta' : 'obraz.galeriaBrakWynikow')}
          </p>
        )}
        {pliki.map((p) => (
          <button
            key={p.nazwaPliku}
            type="button"
            onClick={() => onWybierz(p.nazwaPliku === wybrany ? null : p.nazwaPliku)}
            className={cn(
              'flex w-full items-baseline justify-between gap-3 border-b border-border/60 px-2.5 py-1.5 text-left text-xs last:border-0 hover:bg-accent',
              p.nazwaPliku === wybrany && 'bg-accent',
            )}
          >
            <span className="min-w-0 truncate font-mono">
              {p.nazwaPliku}
              {p.nazwaPliku === obecny && <span className="ml-2 font-sans text-muted-foreground">{t('obraz.galeriaObecny')}</span>}
            </span>
            <span className="shrink-0 text-muted-foreground">
              {/* Liczba towarów mówi o pliku więcej niż jego nazwa — indeksy dostawcy nic nie zdradzają. */}
              {t('obraz.galeriaUzywa').replace('{n}', String(p.uzywaTowarow))}
              {(p.uzywaGrup ?? 0) > 0 && ` · ${t('obraz.galeriaGrup').replace('{n}', String(p.uzywaGrup))}`} ·{' '}
              {Math.round(p.rozmiar / 1024)} kB
            </span>
          </button>
        ))}
      </div>
      {biblioteka.data?.wiecej === true && (
        /** ⚠️ Mówimy, że lista jest UCIĘTA — inaczej „nie ma tego pliku" znaczyłoby dwie różne rzeczy. */
        <p className="text-xs text-muted-foreground">{t('obraz.galeriaWiecej')}</p>
      )}

      {podglad !== null && (
        <div className="flex items-center gap-3 rounded-md border border-border p-3">
          <ObrazekMini groupId={islandId} nazwaPliku={podglad} hasImage rozmiar={128} />
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-xs">{podglad}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('obraz.galeriaWybrany')}</p>
          </div>
          {mozeUsuwac && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="shrink-0 text-destructive"
              disabled={usun.isPending}
              onClick={async () => {
                const ile = szczegoly.data?.towarow ?? pliki.find((p) => p.nazwaPliku === podglad)?.uzywaTowarow ?? 0;
                /** ⚠️ Kafelki grup też tracą obrazek (27.10.2026) — mówimy to w tym samym pytaniu. */
                const grup = pliki.find((p) => p.nazwaPliku === podglad)?.uzywaGrup ?? 0;
                const pytanie =
                  t('obraz.usunPytanie').replace('{plik}', podglad).replace('{n}', String(ile)) +
                  (grup > 0 ? `\n\n${t('obraz.usunGrupy').replace('{n}', String(grup))}` : '');
                if (!(await zapytaj({ tytul: t('obraz.usun'), tresc: pytanie, etykietaPotwierdz: t('obraz.usun'), ton: 'grozne' }))) {
                  return;
                }
                usun.mutate(podglad);
              }}
            >
              {usun.isPending ? <Loader2 className="animate-spin" /> : <Trash2 className="size-4" />}
              {t('obraz.usun')}
            </Button>
          )}
        </div>
      )}

      {/* ── SZCZEGÓŁY PLIKU ──────────────────────────────────────────────────────────── */}
      {zarzadzanie && podglad !== null && szczegoly.data !== undefined && (
        <div className="space-y-2 rounded-md border border-border p-3 text-xs">
          <p className="text-muted-foreground">
            {szczegoly.data.wBibliotece === null
              ? t('obraz.szczegolyBezBajtow')
              : t('obraz.szczegolyPlik')
                  .replace('{kb}', String(Math.round(szczegoly.data.wBibliotece.rozmiar / 1024)))
                  .replace('{zrodlo}', szczegoly.data.wBibliotece.zrodlo)
                  .replace('{kiedy}', new Date(szczegoly.data.wBibliotece.zmieniono).toLocaleString('pl-PL'))}
          </p>

          {/*
            ⚠️ „NA ILU AUTOMATACH LEŻY" TO ODPOWIEDŹ Z OSTATNIEGO SPISU, nie z pytania maszyn na
            żywo — i mówimy też, których NIE zmierzono. „Nie wiem, co tam leży" to inna odpowiedź
            niż „nie ma", a przy decyzji o zmianie nazwy albo usunięciu to właśnie ta różnica jest
            treścią.
          */}
          <p>
            <span className="font-medium">{t('obraz.szczegolyNaAutomatach')}</span>{' '}
            {szczegoly.data.maszyny.length === 0
              ? t('obraz.szczegolyNigdzie')
              : szczegoly.data.maszyny
                  .map((m) => `${m.automat?.nazwa ?? m.nodeId} (${Math.round(m.rozmiar / 1024)} kB)`)
                  .join(', ')}
          </p>
          {szczegoly.data.niezmierzone.length > 0 && (
            <p className="text-muted-foreground">
              {t('obraz.szczegolyNiezmierzone').replace(
                '{maszyny}',
                szczegoly.data.niezmierzone.map((m) => m.automat?.nazwa ?? m.nodeId).join(', '),
              )}
            </p>
          )}

          <p>
            <span className="font-medium">{t('obraz.szczegolyTowary').replace('{n}', String(szczegoly.data.towarow))}</span>
            {szczegoly.data.towary.length > 0 && (
              <span className="text-muted-foreground">
                {' '}
                {szczegoly.data.towary.map((x) => x.nazwa).join(', ')}
                {szczegoly.data.towarow > szczegoly.data.towary.length ? ' …' : ''}
              </span>
            )}
          </p>

          {/* ── ZMIANA NAZWY ─────────────────────────────────────────────────────────── */}
          {mozeUsuwac && (
            <div className="space-y-1.5 border-t border-border pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  className="h-8 min-w-48 flex-1 font-mono text-xs"
                  autoComplete="off"
                  placeholder={podglad}
                  value={nowaNazwa}
                  onChange={(e) => setNowaNazwa(e.target.value)}
                  aria-label={t('obraz.nowaNazwa')}
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={zmienNazwe.isPending || nowaNazwa.trim() === '' || nowaNazwa.trim() === podglad}
                  onClick={async () => {
                    const na = nowaNazwa.trim();
                    const pytanie = (takzePrzypisane ? t('obraz.nazwaPytanieZPrzypisaniem') : t('obraz.nazwaPytanie'))
                      .replace('{z}', podglad)
                      .replace('{na}', na)
                      .replace('{n}', String(szczegoly.data?.towarow ?? 0));
                    if (!(await zapytaj({ tytul: t('obraz.zmienNazwe'), tresc: pytanie, etykietaPotwierdz: t('obraz.zmienNazwe') }))) return;
                    zmienNazwe.mutate({ z: podglad, na, takze: takzePrzypisane });
                  }}
                >
                  {zmienNazwe.isPending ? <Loader2 className="animate-spin" /> : <PencilLine className="size-4" />}
                  {t('obraz.zmienNazwe')}
                </Button>
              </div>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={takzePrzypisane}
                  onChange={(e) => setTakzePrzypisane(e.target.checked)}
                />
                <span>
                  {t('obraz.takzePrzypisane')}
                  <span className="block text-[11px] text-muted-foreground">{t('obraz.takzePrzypisaneOpis')}</span>
                </span>
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
